// src/auth/graphAuth.js
import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { msalInstance, GRAPH_SCOPES } from "./msal";

/**
 * Acquire an access token for Microsoft Graph API.
 *
 * This is separate from the backend API token (API_SCOPE) because
 * an OAuth2 access token can only target a single audience/resource.
 * The backend API token targets "api://2ac1ef87-..." while this
 * targets "https://graph.microsoft.com".
 *
 * Uses the same MSAL instance and account -- just different scopes.
 */
export async function getGraphToken() {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No MSAL account found. User must be logged in via SSO.");
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: GRAPH_SCOPES,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        const popupResult = await msalInstance.acquireTokenPopup({
          account,
          scopes: GRAPH_SCOPES,
        });
        return popupResult.accessToken;
      } catch (popupErr) {
        if (
          popupErr instanceof BrowserAuthError &&
          popupErr.errorCode === "popup_window_error"
        ) {
          // Redirect fallback -- page will navigate away
          msalInstance.acquireTokenRedirect({
            account,
            scopes: GRAPH_SCOPES,
          });
          return null;
        }
        throw popupErr;
      }
    }

    // Handle interaction_in_progress error
    if (
      err instanceof BrowserAuthError &&
      err.errorCode === "interaction_in_progress"
    ) {
      throw new Error("Another authentication is in progress. Please wait and try again.");
    }

    throw err;
  }
}

/**
 * Convenience wrapper: call a Graph API endpoint with Bearer token.
 * Returns the fetch Response object.
 */
export async function graphFetch(url, options = {}) {
  const token = await getGraphToken();
  if (!token) {
    throw new Error("Failed to acquire Graph token");
  }
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  return fetch(url, { ...options, headers });
}
